import * as React from "react";
// @mui
import {
  Grid,
  Box,
  Alert,
  Typography,
  Container,
  TextField,
  CssBaseline,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  MenuItem,
  InputAdornment,
} from "@mui/material";
// icons
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import DomainIcon from "@mui/icons-material/Domain";
import StoreIcon from "@mui/icons-material/Store";
// hooks
import useAuth from "../../hooks/useAuth";
import useIsMountedRef from "../../hooks/useIsMountedRef";
// utils
import merchantCategoryOpts from "../../utils/merchantCategoryOpts";
import countriesOpts from "../../utils/countriesOpts";
//
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";

export default function RegisterForm() {
  const { register } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();

  const [privacyChecked, setPrivacyChecked] = React.useState(false);

  const RegisterSchema = Yup.object().shape({
    name: Yup.string().required(t("yup.errors.name_required")),
    surname: Yup.string().required(t("yup.errors.surname_required")),
    email: Yup.string()
      .email(t("yup.errors.email_not_valid"))
      .required(t("yup.errors.email_required")),
    password: Yup.string().required(t("yup.errors.password_required")),
    password_confirmation: Yup.string().required(
      t("yup.errors.password_confirmation_required")
    ),
    city: Yup.string().required(t("yup.errors.city_required")),
    country: Yup.string().required(t("yup.errors.country_required")),
    company: Yup.string().nullable().notRequired(),
    province: Yup.string().nullable().notRequired(),
    merchant_category: Yup.string().required(
      t("yup.errors.merchant_category_required")
    ),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      surname: "",
      email: "",
      password: "",
      password_confirmation: "",
      city: "",
      country: "",
      company: "",
      province: "",
      merchant_category: "",
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      if (!privacyChecked) {
        setErrors({ afterSubmit: t("yup.errors.privacy_required") });
        return;
      }

      try {
        await register(
          values.name,
          values.surname,
          values.email,
          values.password,
          values.password_confirmation,
          values.city,
          values.country,
          values.company,
          values.province,
          values.merchant_category
        );
        if (isMountedRef.current) {
          setSubmitting(false);
        }

        window.location.href = "/";
      } catch (error) {
        console.error(error.data);

        const errorMessage = Object.values(error.data)[0][0];
        setErrors({ afterSubmit: errorMessage });

        if (isMountedRef.current) {
          if (error.status === 422) {
            values[Object.keys(error.data)[0]] = "";

            if (Object.keys(error.data)[0] === "password") {
              values.password_confirmation = "";
            }

            resetForm({ values: values });
            setErrors({ afterSubmit: errorMessage });
          } else {
            resetForm();
            setErrors({ afterSubmit: t(error.data.message) });
          }

          setSubmitting(false);
        }
      }
    },
  });

  const { errors } = formik;

  const handlePrivacyChange = (evt) => {
    setPrivacyChecked(evt.target.checked);
  };

  return (
    <Container component="div">
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          component="h1"
          variant="h4"
          sx={{ color: "#004990", fontWeight: "bold", mb: 2 }}
        >
          {t("authentication.register.title")}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          {errors.afterSubmit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.afterSubmit}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="name"
                placeholder={t("authentication.fields.name")}
                name="name"
                autoComplete="name"
                autoFocus
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="surname"
                placeholder={t("authentication.fields.surname")}
                name="surname"
                autoComplete="surname"
                autoFocus
                value={formik.values.surname}
                onChange={formik.handleChange}
                error={formik.touched.surname && Boolean(formik.errors.surname)}
                helperText={formik.touched.surname && formik.errors.surname}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="email"
                placeholder={t("authentication.fields.email")}
                name="email"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                {t("authentication.register.password")}
              </Alert>
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                name="password"
                placeholder={t("authentication.fields.password")}
                type="password"
                id="password"
                autoComplete="current-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                name="password_confirmation"
                placeholder={t("authentication.fields.password_confirmation")}
                type="password"
                id="password_confirmation"
                autoComplete="current-password"
                value={formik.values.password_confirmation}
                onChange={formik.handleChange}
                error={
                  formik.touched.password_confirmation &&
                  Boolean(formik.errors.password_confirmation)
                }
                helperText={
                  formik.touched.password_confirmation &&
                  formik.errors.password_confirmation
                }
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="city"
                placeholder={t("authentication.fields.city")}
                name="city"
                autoComplete="city"
                autoFocus
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationCityIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                id="country"
                name="country"
                label={t("authentication.fields.country")}
                value={formik.values.country}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
              >
                {countriesOpts.map((item, index) => (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="province"
                placeholder={t("authentication.fields.province")}
                name="province"
                autoComplete="province"
                autoFocus
                value={formik.values.province}
                onChange={formik.handleChange}
                error={
                  formik.touched.province && Boolean(formik.errors.province)
                }
                helperText={formik.touched.province && formik.errors.province}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                name="merchant_category"
                label={t("authentication.fields.register.merchant_category")}
                value={formik.values.merchant_category}
                onChange={formik.handleChange}
                error={
                  formik.touched.merchant_category &&
                  Boolean(formik.errors.merchant_category)
                }
                helperText={
                  formik.touched.merchant_category &&
                  formik.errors.merchant_category
                }
              >
                {merchantCategoryOpts.map((item, index) => (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="company"
                placeholder={t("authentication.fields.company")}
                name="company"
                autoComplete="company"
                autoFocus
                value={formik.values.company}
                onChange={formik.handleChange}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StoreIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyChecked}
                    onChange={handlePrivacyChange}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={t("authentication.fields.privacy")}
              />
            </Grid>
          </Grid>

          <Stack
            direction="column"
            justifyContent="center"
            spacing={3}
            sx={{ mt: 2, p: "0 25%", textAlign: "center" }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ p: "8px 50px", backgroundColor: "#2CA4DE" }}
            >
              {t("authentication.register.submit")}
            </Button>
          </Stack>
        </form>
      </Box>
    </Container>
  );
}

const styles = {
  inputText: {
    border: "0px solid",
    marginTop: "0",
    borderRadius: "10px",
  },
};
